import React from "react";
import {
  makeStyles,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  Box,
  Radio
} from "@material-ui/core";
import clsx from "clsx";
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker
} from "@material-ui/pickers";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DateFnsUtils from "@date-io/date-fns";
import { Trans } from "react-i18next";
import PhoneInput from "./PhoneInput";
import { remCalc } from "../../utils/helpers";
import SelectTagPopover from "./SelectTagPopover";
import { Autocomplete } from "@material-ui/lab";
import VehicleAutocomplete from "../project/VehicleAutocomplete";

const useStyles = makeStyles(theme => ({
  inputRow: {
    padding: theme.spacing(0),
    flexWrap: "nowrap"
  },
  inputItem: {
    padding: `${theme.spacing(0.5, 1)} !important`
  },
  inputField: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    height: theme.spacing(8.75),
    fontSize: "0.9375rem",
    "& + .MuiTextField-root": {
      marginLeft: theme.spacing(2)
    },
    "& .Mui-disabled": {
      cursor: theme.cursors.notAllowed
    }
  },
  optionGridItem: {
    padding: `${theme.spacing(0, 1.5, 1)} !important`
  },
  leftPadding: {
    paddingLeft: `${theme.spacing(1)}px !important`
  },
  rightPadding: {
    paddingRight: `${theme.spacing(1)}px !important`
  },
  topMargin: {
    "&:first-of-type": {
      marginTop: theme.spacing(2)
    }
  },
  timeWindow: {
    "& button": {
      padding: theme.spacing(0.5)
    },
    "& svg": {
      color: "#808080",
      width: "1rem",
      height: "1rem"
    },
    "& > div:focus-within svg": {
      color: "#202124"
    }
  },
  timeWindowSeparatorLine: {
    width: 16,
    textAlign: "center",
    color: "#DBDCE1",
    fontSize: 10
  },
  hourInputs: {
    marginTop: `${theme.spacing(1)}px !important`
  },
  customHelperText: {
    fontSize: "0.6875rem",
    lineHeight: "0.8125rem",
    letterSpacing: 0
  },
  inputLabel: {
    fontSize: "0.9375rem",
    "& b": {
      fontWeight: 500
    }
  },
  selectBox: {
    maxHeight: remCalc(500)
  },
  popover: {
    zIndex: "1302 !important"
  },
  borderStyle: {
    "& .MuiInput-underline.Mui-disabled:before": {
      borderBottomStyle: "solid"
    }
  },
  noHeight: {
    height: "unset"
  },
  optionName: {
    width: "calc(50% - 8px)"
  },
  optionEmail: {
    width: "calc(50% - 8px)",
    textAlign: "right"
  },
  inputHover: {
    "&:hover *": {
      cursor: "pointer"
    }
  },
  selectOptionDescription: {
    fontSize: theme.typography.pxToRem(15),
    color: "#7D7E82"
  },
  topLabel: {
    color: "#4D4D50",
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    marginTop: theme.spacing(0.5)
  },
  noHelperText: {
    height: "unset"
  },
  selectRadio: {
    paddingLeft: 0,
    paddingRight: 16,
    "&$selectRadioChecked .MuiSvgIcon-root": {
      color: theme.palette.primary.main
    }
  },
  selectRadioChecked: {},
  menuItemSelected: {
    backgroundColor: "#F8F9FF !important"
  }
}));

export default function Form({
  data,
  inputValues,
  errorValues,
  selectValues,
  countryCode,
  handleChange,
  handlePhoneChange,
  showPassword,
  published,
  inputRef,
  settings,
  editStop
}) {
  const classes = useStyles();

  return (
    data &&
    data.map((item, index) => {
      return (
        <Grid
          container
          key={index}
          className={clsx({
            [classes.inputRow]: !settings
          })}
          spacing={2}
        >
          {item.map((el, i) => {
            return (
              <Grid
                item
                xs={settings ? 6 : 12}
                key={el.name}
                className={clsx(classes.inputItem, {
                  [classes.optionGridItem]: settings,
                  [classes.leftPadding]: settings && i === 0,
                  [classes.rightPadding]: settings && i === 1
                })}
              >
                {el.componentType && el.componentType === "phone" ? (
                  <PhoneInput
                    editStop
                    key={el.name}
                    dataCy={el.dataCy}
                    label={el.label}
                    countryCode={countryCode}
                    published={published}
                    phoneError={errorValues[el.name]}
                    phoneNumber={inputValues[el.name]}
                    handlePhoneChange={handlePhoneChange}
                    disabled={el.disabled}
                  />
                ) : el.componentType === "select" ? (
                  <>
                    {el.topLabel && (
                      <Typography className={classes.topLabel}>
                        {el.topLabel}
                      </Typography>
                    )}
                    <FormControl
                      key={el.name}
                      margin="normal"
                      variant="outlined"
                      className={clsx(classes.inputField, {
                        [classes.topMargin]: el.topMargin,
                        [classes.borderStyle]: editStop,
                        [classes.noHelperText]: el.noHelperText
                      })}
                      error={
                        (errorValues && Boolean(errorValues[el.name])) ||
                        el.error
                      }
                    >
                      <InputLabel
                        id={el.labelId}
                        shrink={el.shrink}
                        classes={{
                          root: classes.inputLabel
                        }}
                        className={clsx({ "Mui-disabled": el.disabled })}
                      >
                        {el.label}
                      </InputLabel>
                      <Select
                        labelId={el.labelId}
                        id={el.selectId}
                        value={inputValues[el.name] || selectValues[el.name]}
                        name={el.name}
                        fullWidth
                        disabled={el.disabled}
                        onChange={el.onChange}
                        className={classes.selectWrapper}
                        MenuProps={{
                          classes: {
                            root: classes.selectWrapper,
                            paper: classes.selectBox
                          },
                          PopoverClasses: {
                            root: clsx(
                              classes.popover,
                              el.classes &&
                                el.classes.PopoverClasses &&
                                el.classes.PopoverClasses.root
                            )
                          }
                        }}
                        label={el.label}
                        renderValue={
                          el.showOnlyOptionLabel
                            ? selectedValue => {
                                const selectedOption = el.options.find(
                                  option => option[0] === selectedValue
                                );
                                if (selectedOption) {
                                  return selectedOption[1];
                                }
                                return "";
                              }
                            : undefined
                        }
                      >
                        {el.choose && (
                          <MenuItem value="" disabled>
                            Choose...
                          </MenuItem>
                        )}
                        {el.options.map(option => (
                          <MenuItem
                            value={option[0]}
                            key={option[1]}
                            style={{
                              flexWrap: "nowrap",
                              flexDirection: "column",
                              alignItems: "flex-start"
                            }}
                            classes={{ selected: classes.menuItemSelected }}
                          >
                            {el.i18nKey ? (
                              <Trans i18nKey={option[2]}>{option[1]}</Trans>
                            ) : (
                              <Grid container wrap="nowrap">
                                <Grid item>
                                  {el.showRadioButton && (
                                    <Radio
                                      classes={{
                                        root: classes.selectRadio,
                                        checked: classes.selectRadioChecked
                                      }}
                                      checked={
                                        option[0] ===
                                        (inputValues[el.name] ||
                                          selectValues[el.name])
                                      }
                                    />
                                  )}
                                </Grid>
                                <Grid item xs>
                                  {option[1]}
                                  {option[2] && (
                                    <div
                                      className={
                                        classes.selectOptionDescription
                                      }
                                    >
                                      {option[2]}
                                    </div>
                                  )}
                                </Grid>
                              </Grid>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {(errorValues && errorValues[el.name]) ||
                          el.required ||
                          el.helperText}
                      </FormHelperText>
                    </FormControl>
                  </>
                ) : el.componentType === "tagSelect" ? (
                  <SelectTagPopover
                    el={el}
                    item={item}
                    // disabled={disabled}
                    classes={classes}
                    editStop={editStop}
                    selectValues={selectValues}
                    inDialog={el.inDialog}
                  />
                ) : el.componentType === "timeWindow" ? (
                  <MuiPickersUtilsProvider
                    key={el.componentType}
                    utils={DateFnsUtils}
                  >
                    <Grid container wrap="nowrap">
                      <Grid item xs={6}>
                        <>
                          {el.startTopLabel && (
                            <Typography className={classes.topLabel}>
                              {el.startTopLabel}
                            </Typography>
                          )}
                        </>
                        <KeyboardTimePicker
                          margin="normal"
                          format="HH:mm"
                          inputRef={el.inputRefStart}
                          onBlur={el.onBlurStart}
                          label={el.startLabel}
                          value={inputValues[el.startName] || null}
                          onChange={el.onChangeStart}
                          onKeyDown={el.onKeyDownStart}
                          className={clsx(
                            classes.inputField,
                            classes.hourInputs,
                            classes.timeWindow,
                            {
                              [classes.borderStyle]: editStop
                            }
                          )}
                          KeyboardButtonProps={{
                            style: {
                              display: "none"
                            }
                          }}
                          inputProps={{ "data-cy": el.startDataCy }}
                          inputVariant="outlined"
                          keyboardIcon={<AccessTimeIcon />}
                          helperText={
                            errorValues[el.startName] || el.requiredStart
                          }
                          error={!!errorValues[el.startName]}
                          disabled={el.disabled}
                          placeholder={el.startPlaceholder}
                          InputLabelProps={{
                            classes: {
                              root: classes.inputLabel
                            }
                          }}
                        />
                      </Grid>
                      <Grid
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center"
                        }}
                        item
                      >
                        <Typography className={classes.timeWindowSeparatorLine}>
                          —
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <>
                          {el.endTopLabel && (
                            <Typography className={classes.topLabel}>
                              {el.endTopLabel}
                            </Typography>
                          )}
                        </>
                        <KeyboardTimePicker
                          margin="normal"
                          format="HH:mm"
                          inputRef={el.inputRefEnd}
                          onBlur={el.onBlurEnd}
                          label={el.endLabel}
                          value={inputValues[el.endName] || null}
                          inputProps={{
                            "data-cy": el.endDataCy
                          }}
                          onChange={el.onChangeEnd}
                          onKeyDown={el.onKeyDownEnd}
                          className={clsx(
                            classes.inputField,
                            classes.hourInputs,
                            classes.timeWindow,
                            {
                              [classes.borderStyle]: editStop
                            }
                          )}
                          KeyboardButtonProps={{
                            style: {
                              display: "none"
                            }
                          }}
                          keyboardIcon={<AccessTimeIcon />}
                          helperText={errorValues[el.endName] || el.requiredEnd}
                          error={
                            !!errorValues[el.startName] ||
                            !!errorValues[el.endName]
                          }
                          disabled={el.disabled}
                          inputVariant="outlined"
                          placeholder={el.endPlaceholder}
                          InputLabelProps={{
                            classes: {
                              root: classes.inputLabel
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MuiPickersUtilsProvider>
                ) : el.componentType === "autoComplete" ? (
                  <Autocomplete
                    id={el.id}
                    options={el.options}
                    getOptionLabel={option => {
                      return option.name;
                    }}
                    classes={{
                      popper: classes.popover,
                      option: classes.listBoxOption
                    }}
                    renderOption={option => (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width={"100%"}
                      >
                        <Typography noWrap className={classes.optionName}>
                          {option.name}
                        </Typography>
                        <Typography
                          color="secondary"
                          noWrap
                          className={classes.optionEmail}
                        >
                          {option.email}
                        </Typography>
                      </Box>
                    )}
                    value={inputValues[el.name]}
                    name={el.name}
                    onChange={el.onChange}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={el.label}
                        className={clsx(classes.inputField)}
                        variant="outlined"
                      />
                    )}
                  />
                ) : el.componentType === "vehicleAutoComplete" ? (
                  <VehicleAutocomplete
                    commonElement
                    inputId={el.id}
                    label={el.label}
                    topLabel={el.topLabel}
                    width={el.width}
                    initialValue={
                      inputValues[el.name]
                        ? inputValues[el.name].formatted_address
                        : ""
                    }
                    handleSelect={el.handleAddressSelect}
                    error={
                      (errorValues && Boolean(errorValues[el.name])) || el.error
                    }
                    helperText={
                      (errorValues && errorValues[el.name]) ||
                      el.required ||
                      el.helperText
                    }
                    placeholder={el.placeholder || ""}
                    disabled={el.disabled}
                    noMarginTop={el.noMarginTop}
                  />
                ) : (
                  el.renderInputField && (
                    <>
                      {el.topLabel && (
                        <Typography className={classes.topLabel}>
                          {el.topLabel}
                        </Typography>
                      )}
                      <TextField
                        variant="outlined"
                        id={el.id}
                        key={el.name}
                        label={el.label}
                        name={el.name}
                        ref={el.ref ? inputRef : null}
                        inputRef={el.inputRef}
                        type={showPassword ? "text" : el.type}
                        inputProps={{
                          "data-cy": el.dataCy,
                          min: el.min && el.min,
                          max: el.max && el.max,
                          maxLength: el.maxLength && el.maxLength,
                          autoComplete: el.autoComplete && el.autoComplete,
                          readOnly: el.readOnly
                        }}
                        className={clsx(classes.inputField, {
                          [classes.topMargin]: el.topMargin,
                          [classes.borderStyle]: editStop,
                          [classes.noHeight]: el.multiline,
                          [classes.inputHover]: el.onClick
                        })}
                        value={
                          (inputValues && inputValues[el.name]) ||
                          (el.value ? el.value : "")
                        }
                        onChange={e => {
                          handleChange && handleChange(e);
                          el.onChange && el.onChange(e);
                        }}
                        onBlur={el.onBlur}
                        onFocus={el.onFocus}
                        onKeyPress={el.onKeyPress}
                        onKeyDown={el.onKeyDown}
                        onKeyUp={el.onKeyUp}
                        onPaste={el.onPaste}
                        onClick={el.onClick}
                        margin="normal"
                        error={
                          (errorValues && Boolean(errorValues[el.name])) ||
                          el.error
                        }
                        helperText={
                          (errorValues && errorValues[el.name]) ||
                          el.required ||
                          el.helperText
                        }
                        placeholder={el.placeholder || ""}
                        fullWidth
                        disabled={el.disabled}
                        autoFocus={el.autoFocus}
                        multiline={el.multiline}
                        rowsMax="10"
                        InputProps={{
                          endAdornment: el.endAdornment
                        }}
                        InputLabelProps={{
                          shrink: el.shrink,
                          required: false,
                          className: classes.inputLabel
                        }}
                        FormHelperTextProps={{
                          classes: {
                            root: classes.customHelperText
                          }
                        }}
                      />
                    </>
                  )
                )}
              </Grid>
            );
          })}
        </Grid>
      );
    })
  );
}
